/**
 * Helpers for post-email-verification navigation.
 */
import {
  academyPublicBaseUrl,
  extractAcademySlugFromPath,
  extractTenantSubdomainLabelFromUrl,
  hasRole,
  isBrandedSubdomainActive,
  ORG_PUBLIC_PATH_PREFIX,
  type Academy,
  type User,
} from '@court-sync/shared';
import { buildOrgLoginPath, type OrgPathFields } from '@/lib/orgPaths';

export type ParsedReturnUrl = {
  /** Same-origin path+search, or full URL when cross-origin */
  href: string;
  /** Path + search without origin */
  path: string;
  /** True when returnUrl targets a different origin than currentOrigin */
  isCrossOrigin: boolean;
  /** Origin when returnUrl is absolute */
  origin?: string;
};

export type AcademyLoginFields = Pick<
  Academy,
  'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'
>;

export type PostVerificationLoginTarget = {
  href: string;
  external: boolean;
};

export type PostVerificationRedirectAction =
  | { type: 'external'; href: string }
  | { type: 'navigate'; href: string; state?: { email?: string; message?: string } };

export const ALLOWED_LOGIN_BANNER_MESSAGE_PREFIXES = [
  'Email verified successfully!',
  'Please verify your email',
] as const;

/** Only allow known post-verification banner messages from query params. */
export function sanitizeLoginBannerMessage(message: string | null | undefined): string | undefined {
  const trimmed = message?.trim();
  if (!trimmed || trimmed.length > 200) {
    return undefined;
  }
  return ALLOWED_LOGIN_BANNER_MESSAGE_PREFIXES.some((prefix) => trimmed.startsWith(prefix))
    ? trimmed
    : undefined;
}

export function parseReturnUrl(returnUrl: string, currentOrigin = ''): ParsedReturnUrl {
  if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
    const parsed = new URL(returnUrl);
    const path = `${parsed.pathname}${parsed.search}`;
    const isCrossOrigin = Boolean(currentOrigin && parsed.origin !== currentOrigin);
    return {
      path,
      href: isCrossOrigin ? returnUrl : path,
      isCrossOrigin,
      origin: parsed.origin,
    };
  }
  const path = returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`;
  return { path, href: path, isCrossOrigin: false };
}

export function getReturnPath(returnUrl: string, currentOrigin?: string): string {
  return parseReturnUrl(returnUrl, currentOrigin).path;
}

export function extractAcademySlugFromReturnPath(path: string): string | null {
  return extractAcademySlugFromPath(path) ?? null;
}

export function extractOrgSlugFromReturnPath(path: string): string | null {
  const match = path.match(new RegExp(`^${ORG_PUBLIC_PATH_PREFIX}/([^/]+)`));
  return match?.[1] ?? null;
}

export function isOrgMemberReturnPath(path: string): boolean {
  return path.startsWith(`${ORG_PUBLIC_PATH_PREFIX}/`);
}

const ORG_SUBDOMAIN_MEMBER_PATH =
  /^\/(join|membership|dashboard|play|court-hire|settings|bookings|book|book-private)(\/|$|\?)/;

/** Whether post-verification sign-in should use org login (not platform /login). */
export function isOrgLoginReturnPath(path: string, orgSlug: string | null): boolean {
  if (!orgSlug) return false;
  if (isOrgMemberReturnPath(path)) {
    const slugFromPath = extractOrgSlugFromReturnPath(path);
    return !slugFromPath || slugFromPath === orgSlug;
  }
  return ORG_SUBDOMAIN_MEMBER_PATH.test(path);
}

/** Branded tenant label from an absolute academy subdomain return URL. */
export function extractAcademySubdomainLabelFromReturnUrl(returnUrl: string): string | null {
  return extractTenantSubdomainLabelFromUrl(returnUrl) ?? null;
}

export function resolveEffectiveAcademySlug(options: {
  resolvedAcademySlug?: string | null;
  academySlugParam?: string | null;
  academySubdomainParam?: string | null;
  subdomainLabel?: string | null;
}): string | null {
  return (
    options.resolvedAcademySlug?.trim() ||
    options.academySlugParam?.trim() ||
    options.academySubdomainParam?.trim() ||
    options.subdomainLabel?.trim() ||
    null
  );
}

export function resolveEmailVerificationAcademySlug(options: {
  academySlugParam?: string | null;
  returnUrl?: string | null;
  currentOrigin?: string;
}): string | null {
  if (options.academySlugParam?.trim()) {
    return options.academySlugParam.trim();
  }
  if (!options.returnUrl?.trim()) {
    return null;
  }
  return extractAcademySlugFromReturnPath(getReturnPath(options.returnUrl, options.currentOrigin));
}

const PLATFORM_ONBOARDING_PATH_PREFIXES = [
  '/academy-setup',
  '/finish-signup',
  '/payment',
  '/subscription-checkout-success',
  '/payment/success',
] as const;

/** Platform operator routes where email verification is deferred until after checkout. */
export function isPlatformOnboardingPath(path: string): boolean {
  const normalized = path.split('?')[0];
  return PLATFORM_ONBOARDING_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}

/**
 * Org operators may complete signup, onboarding, and Stripe checkout before verifying email.
 * Returns true when the verify-email screen should not block the current step.
 */
export function shouldDeferPlatformEmailVerification(
  user: User | null,
  options?: { returnUrl?: string | null; hasActiveSubscription?: boolean }
): boolean {
  if (!user) {
    return false;
  }

  const returnPath = options?.returnUrl
    ? parseReturnUrl(options.returnUrl).path
    : null;
  if (returnPath && isPlatformOnboardingPath(returnPath)) {
    return true;
  }

  const isOrgOperator = hasRole(user, 'academy_owner') || hasRole(user, 'club_owner');
  if (!isOrgOperator) {
    return false;
  }

  if (hasRole(user, 'academy_owner') && !user.academyId) {
    return true;
  }

  if (options?.hasActiveSubscription === false) {
    return true;
  }

  return false;
}

const PATH_MODE_BOOKING = /^\/academy\/[^/]+\/(book|book-lesson)/;
const SUBDOMAIN_BOOKING_PATH = /^\/book(\/|$)/;
const SUBDOMAIN_PRIVATE_BOOKING_PATH = /^\/book-private/;

/** Routes the user may enter while still signed in immediately after applyActionCode. */
export function isAuthenticatedPostVerificationRoute(path: string): boolean {
  if (path === '/dashboard' || path.startsWith('/dashboard/')) return true;
  if (path.startsWith('/coach/')) return true;
  if (/^\/academy\/[^/]+\/coach/.test(path)) return true;
  if (/^\/academy\/[^/]+\/dashboard/.test(path)) return true;
  if (path === '/finish-signup' || path.startsWith('/finish-signup?')) return true;
  if (path === '/admin/onboarding' || path.startsWith('/admin/onboarding?')) return true;
  if (path === '/admin/dashboard' || path.startsWith('/admin/dashboard')) return true;
  if (PATH_MODE_BOOKING.test(path)) return true;
  if (SUBDOMAIN_BOOKING_PATH.test(path)) return true;
  if (SUBDOMAIN_PRIVATE_BOOKING_PATH.test(path)) return true;
  if (/^\/club-academy\/[^/]+\/(join|membership|dashboard|play|court-hire|settings|bookings)(\/|$)/.test(path)) {
    return true;
  }
  if (path === '/join' || path.startsWith('/join?')) return true;
  if (path === '/membership' || path.startsWith('/membership?')) return true;
  return false;
}

const SUBDOMAIN_GUARDIAN_PATH_PATTERNS = [
  /^\/dashboard(\/|$)/,
  /^\/bookings(\/|$)/,
  SUBDOMAIN_BOOKING_PATH,
  SUBDOMAIN_PRIVATE_BOOKING_PATH,
  /^\/lessons(\/|$)/,
  /^\/register(\/|$)/,
] as const;

function isSubdomainGuardianPath(path: string): boolean {
  return SUBDOMAIN_GUARDIAN_PATH_PATTERNS.some((pattern) => pattern.test(path));
}

export function isAcademyGuardianReturnPath(path: string, academySlug?: string | null): boolean {
  if (path.includes('/coach/')) return false;
  if (/^\/academy\/[^/]+/.test(path)) return true;
  if (academySlug && isSubdomainGuardianPath(path)) return true;
  return false;
}

/** Whether post-verification sign-in should use academy login (not platform /login). */
export function isAcademyLoginReturnPath(path: string, academySlug: string | null): boolean {
  if (!academySlug) return false;
  if (/^\/admin(\/|$)/.test(path)) return false;
  if (/^\/academy-owner(\/|$)/.test(path)) return false;
  if (/^\/academy\/[^/]+(\/|$)/.test(path)) return true;
  if (/^\/coach(\/|$)/.test(path) && path !== '/coach/academy-picker') return true;
  return isAcademyGuardianReturnPath(path, academySlug);
}

export function resolveAcademyLoginDashboardType(path: string): 'student' | 'coach' {
  return path.includes('/coach/') ? 'coach' : 'student';
}

function appendQueryParams(href: string, params: Record<string, string | undefined>): string {
  const isAbsolute = href.startsWith('http://') || href.startsWith('https://');
  const url = isAbsolute ? new URL(href) : new URL(href, 'https://app.courtsync.io');

  for (const [key, value] of Object.entries(params)) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }

  return isAbsolute ? url.toString() : `${url.pathname}${url.search}`;
}

export function buildPostVerificationOrgLoginTarget(options: {
  org: OrgPathFields;
  returnPath: string;
  email?: string | null;
  message?: string | null;
  hostMode?: 'path' | 'subdomain';
}): PostVerificationLoginTarget {
  const href = buildOrgLoginPath(options.org, options.returnPath, options.hostMode ?? 'path');
  const withBanner = appendQueryParams(href, {
    email: options.email?.trim() || undefined,
    message: options.message?.trim() || undefined,
  });
  return { href: withBanner, external: false };
}

/** Build platform login path for post-verification flows. */
export function buildPlatformLoginHref(
  returnPath?: string,
  email?: string | null,
  message?: string | null
): string {
  const params: Record<string, string | undefined> = {
    returnUrl: returnPath,
    email: email?.trim() || undefined,
    message: message?.trim() || undefined,
  };
  return appendQueryParams('/login', params);
}

/** Build academy login URL (path-mode on app host or absolute branded subdomain). */
export function buildPostVerificationLoginTarget(options: {
  academy: AcademyLoginFields | null;
  academySlug: string;
  returnPath: string;
  tenantOrigin?: string;
  email?: string | null;
  message?: string | null;
  dashboardType?: 'student' | 'coach';
}): PostVerificationLoginTarget {
  const {
    academy,
    academySlug,
    returnPath,
    tenantOrigin,
    email,
    message,
    dashboardType = 'student',
  } = options;
  const fields = academy ?? { slug: academySlug };
  const useBrandedSubdomain = academy ? isBrandedSubdomainActive(academy) : false;

  if (useBrandedSubdomain && academy) {
    const href = appendQueryParams(academyPublicBaseUrl(academy, '/login', {}, 'subdomain'), {
      returnUrl: returnPath,
      dashboard: dashboardType,
      email: email?.trim() || undefined,
      message: message?.trim() || undefined,
    });
    return { href, external: true };
  }

  if (tenantOrigin) {
    const href = appendQueryParams(`${tenantOrigin}/login`, {
      returnUrl: returnPath,
      dashboard: dashboardType,
      email: email?.trim() || undefined,
      message: message?.trim() || undefined,
    });
    return { href, external: true };
  }

  const loginPath = `/academy/${fields.slug}/login`;
  const href = appendQueryParams(
    returnPath ? `${loginPath}?returnUrl=${encodeURIComponent(returnPath)}` : loginPath,
    {
      dashboard: dashboardType,
      email: email?.trim() || undefined,
      message: message?.trim() || undefined,
    }
  );
  return { href, external: false };
}

/**
 * Resolve post-verification redirect for unsigned users (or before session is active).
 * Handles cross-origin tenant URLs and academy login paths.
 */
export function resolveUnsignedPostVerificationRedirect(options: {
  parsed: ParsedReturnUrl;
  effectiveAcademySlug: string | null;
  effectiveOrgSlug?: string | null;
  org?: OrgPathFields | null;
  academy: AcademyLoginFields | null;
  email?: string | null;
  loginMessage: string;
  orgHostMode?: 'path' | 'subdomain';
}): PostVerificationRedirectAction | null {
  const {
    parsed,
    effectiveAcademySlug,
    effectiveOrgSlug,
    org,
    academy,
    email,
    loginMessage,
    orgHostMode = 'path',
  } = options;
  const returnPath = parsed.path;
  const orgSlug = effectiveOrgSlug ?? extractOrgSlugFromReturnPath(returnPath);

  if (orgSlug && org && isOrgLoginReturnPath(returnPath, orgSlug)) {
    const loginTarget = buildPostVerificationOrgLoginTarget({
      org,
      returnPath,
      email,
      message: loginMessage,
      hostMode: orgHostMode,
    });
    if (parsed.isCrossOrigin) {
      return { type: 'external', href: `${parsed.origin ?? ''}${loginTarget.href}` };
    }
    return {
      type: 'navigate',
      href: loginTarget.href,
      state: { email: email ?? undefined, message: loginMessage },
    };
  }

  if (parsed.isCrossOrigin && parsed.origin) {
    if (effectiveAcademySlug && isAcademyLoginReturnPath(returnPath, effectiveAcademySlug)) {
      const loginTarget = buildPostVerificationLoginTarget({
        academy,
        academySlug: effectiveAcademySlug,
        returnPath,
        tenantOrigin: parsed.origin,
        email,
        message: loginMessage,
        dashboardType: resolveAcademyLoginDashboardType(returnPath),
      });
      return { type: 'external', href: loginTarget.href };
    }
    return { type: 'external', href: parsed.href };
  }

  if (effectiveAcademySlug && isAcademyLoginReturnPath(returnPath, effectiveAcademySlug)) {
    const loginTarget = buildPostVerificationLoginTarget({
      academy,
      academySlug: effectiveAcademySlug,
      returnPath,
      email,
      message: loginMessage,
      dashboardType: resolveAcademyLoginDashboardType(returnPath),
    });
    if (loginTarget.external) {
      return { type: 'external', href: loginTarget.href };
    }
    return {
      type: 'navigate',
      href: loginTarget.href,
      state: { email: email ?? undefined, message: loginMessage },
    };
  }

  return null;
}

export function buildEmailVerificationPageUrl(options: {
  email: string;
  returnUrl: string;
  academySlug?: string;
}): string {
  const params = new URLSearchParams({
    email: options.email,
    returnUrl: options.returnUrl,
  });
  if (options.academySlug) {
    params.set('academySlug', options.academySlug);
  }
  return `/email-verification?${params.toString()}`;
}

export function buildResendVerificationReturnUrl(
  returnUrl: string | undefined,
  fallbackPath: string,
  origin: string
): string {
  if (!returnUrl) {
    return `${origin}${fallbackPath}`;
  }
  if (returnUrl.startsWith('http://') || returnUrl.startsWith('https://')) {
    return returnUrl;
  }
  const path = returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`;
  return `${origin}${path}`;
}
